import { createContext, useContext, useLayoutEffect, useRef } from 'react';

/**
 * Carries the single persistent terminal host element (created in
 * `DynamicLayout`) down to whichever layout is active. The terminal is rendered
 * once into this detached element via a portal; the active layout re-parents the
 * element into its slot. Sharing one instance is what lets the terminal survive
 * the mobile <-> desktop layout swap without dropping its SSH connection.
 */
export const TerminalHostContext = createContext<HTMLElement | null>(null);

interface TerminalSlotProps {
  className?: string;
}

/**
 * Placeholder that adopts the shared terminal host element on mount. Moving a
 * DOM node with `appendChild` re-parents it without unmounting the React subtree
 * inside it, so the terminal's xterm buffer and socket stay alive across a
 * layout swap. Both `MobileLayout` and `DragDropLayout` render one of these
 * where the terminal should appear.
 *
 * INVARIANT: exactly one `TerminalSlot` may be mounted at a time. `DynamicLayout`
 * renders only one layout (mobile XOR desktop), so a swap unmounts one slot and
 * mounts the other, which re-adopts `host`. The adopt effect keys on `[host]`
 * (runs on mount), so if two slots were ever mounted at once the second would
 * steal `host` and the first would be left permanently empty.
 */
export function TerminalSlot({ className }: TerminalSlotProps) {
  const host = useContext(TerminalHostContext);
  const slotRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (slot && host && host.parentElement !== slot) {
      slot.appendChild(host);
    }
  }, [host]);

  return <div ref={slotRef} className={className} />;
}
